import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Typeahead, Hint, Highlighter } from "react-bootstrap-typeahead";
import Form from "react-bootstrap/Form";
import classNames from "classnames";
import { InputPropTypes } from "../../../propTypes";
import InputGroup from "../../InputGroup";
import downArrow from "../../../../../assets/down-arrow.svg";

import "./styles.scss";

// Bypassing default FE filtering in async autocomplete
const filterByReturnAll = () => true;

export const DropdownAutocomplete = ({
  label,
  /* Props to be applied directly to the input. onBlur, onChange, onFocus, and onKeyDown are ignored. */
  input: { onFocus, onBlur, onChange, onKeyDown, ...input },
  disabled,
  readonly,
  typeaheadRef,
  inputRef,
  id,
  onInputChange,
  options,
  onSelectionChange,
  onMenuToggle,
  labelKey,
  optionLabelMapper,
  maxLength,
  withoutHint,
  rounded,
  name,
  filterBy,
}) => {
  const autocompleteRef = useRef(null);
  const [isChangeInProgress, setIsChangeInProgress] = useState(false);

  useEffect(() => {
    if (inputRef) {
      if (autocompleteRef.current) {
        // set only native input ref on top of general interface
        inputRef.current = autocompleteRef.current.getInput();
        return;
      }
      inputRef.current = autocompleteRef.current;
    }
  }, [autocompleteRef.current]);

  // Autocomplete keeps "value" in internal state
  // When value become empty in props - Autocomplete does not set it in internal state
  useEffect(() => {
    if (
      !input.value &&
      autocompleteRef.current &&
      autocompleteRef.current.state.text
    ) {
      autocompleteRef.current.clear();
    } else if (autocompleteRef.current) {
      const updateValues =
        autocompleteRef.current && autocompleteRef.current.state.isFocused
          ? { text: input.value, selected: [] }
          : { text: input.value, selected: [], showMenu: false };

      autocompleteRef.current.setState(updateValues);
    }
  }, [input.value]);

  // Logic to handle showing unfiltered options on focus, but not on change (next 3 handlers)
  const handleBlur = useCallback(
    e => {
      setIsChangeInProgress(false);
      onBlur(e);
    },
    [setIsChangeInProgress, onBlur]
  );

  const handleFocus = useCallback(
    e => {
      e.target.select();
      setIsChangeInProgress(false);
      onFocus(e);
    },
    [setIsChangeInProgress, onFocus]
  );

  const handleInputChange = useCallback(
    onChangeFn => e => {
      setIsChangeInProgress(true);
      onChangeFn(e);
    },
    [setIsChangeInProgress]
  );

  const handleMenuToggle = isOpen => {
    if (isOpen) {
      const idElement = document.getElementById(id);
      const element = idElement?.getElementsByClassName("rbt-highlight-text");
      // parentElement in this case is list item, "rbt-highlight-text" is applied directly to the text inside
      element?.[0]?.parentElement?.scrollIntoView({
        behavior: "auto",
        block: "nearest",
        inline: "nearest",
      });
    }

    if (onMenuToggle) {
      onMenuToggle(isOpen);
    }
  };

  const renderInput = useCallback(
    ({ inputRef, referenceElementRef, ...inputProps }) => {
      const WrapperComponent = withoutHint ? React.Fragment : Hint;

      return (
        <WrapperComponent>
          <Form.Control
            {...inputProps}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onChange={handleInputChange(inputProps.onChange)}
            className="rbt-input-main"
            // Workaround to disable default form autocomplete
            autoComplete="new-password"
            maxLength={maxLength}
            ref={node => {
              inputRef(node);
              referenceElementRef(node);
            }}
            name={name}
            disabled={disabled || readonly}
          />
        </WrapperComponent>
      );
    },
    [
      withoutHint,
      maxLength,
      handleBlur,
      handleFocus,
      handleInputChange,
      name,
      disabled,
      readonly,
    ]
  );

  const dropdownStylingAppend = useMemo(() => {
    return (
      <button
        type="button"
        onClick={() => {
          autocompleteRef.current.focus();
          autocompleteRef.current.toggleMenu();
        }}
        tabIndex={-1}
        disabled={disabled || readonly}
      >
        <img src={downArrow} alt="open dropdown" />
      </button>
    );
  }, [autocompleteRef, disabled, readonly]);

  const renderMenuItemChildren = useMemo(
    () =>
      // For now only dropdown alike autocmplete needs highlight
      !isChangeInProgress
        ? (option, props, idx) => (
            <Highlighter search={props.text}>
              {typeof props.labelKey === "function"
                ? props.labelKey(option)
                : option[props.labelKey]}
            </Highlighter>
          )
        : optionLabelMapper,
    [isChangeInProgress, optionLabelMapper]
  );

  return (
    <InputGroup
      className={classNames(
        "mydpd-dropdown-autocomplete",
        rounded && "rounded"
      )}
      append={dropdownStylingAppend}
    >
      <Typeahead
        ref={node => {
          [autocompleteRef, typeaheadRef].forEach(ref => {
            if (typeof ref === "function") {
              ref(node);
            } else if (typeof ref === "object") {
              if (!ref.hasOwnProperty("current")) {
                throw new Error("Unexpected ref object provided");
              }

              ref.current = node;
            }
          });
        }}
        // Unfiltered list of options is shown on focus
        filterBy={!isChangeInProgress ? filterByReturnAll : filterBy}
        id={id}
        onInputChange={onInputChange}
        options={options}
        placeholder={label}
        onKeyDown={onKeyDown}
        onChange={selection => {
          if (selection[0]) {
            onSelectionChange(selection[0]);
          }
        }}
        onMenuToggle={handleMenuToggle}
        maxResults={0}
        labelKey={labelKey}
        disabled={disabled || readonly}
        renderMenuItemChildren={renderMenuItemChildren}
        renderInput={renderInput}
        inputProps={input}
        paginate={false}
        highlightOnlyResult={true}
      />
    </InputGroup>
  );
};

DropdownAutocomplete.propTypes = {
  id: PropTypes.string.isRequired,
  typeaheadRef: PropTypes.any,
  withoutHint: PropTypes.bool,
  onInputChange: PropTypes.func,
  options: PropTypes.array,
  onSelectionChange: PropTypes.func,
  onMenuToggle: PropTypes.func,
  labelKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  optionLabelMapper: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  maxLength: PropTypes.number,
  ...InputPropTypes,
};
